import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface Ticket {
  id: string
  subject: string
  description: string
  status: string
  priority: string
  category: string
  customer_email: string
  assigned_to: string | null
  created_at: string
  updated_at: string
}

export default function TicketingPage() {
  const [tickets, setTickets] = useState<Ticket[]>([])
  const [statistics, setStatistics] = useState<any>(null)
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null)
  const [comments, setComments] = useState<any[]>([])
  const [filterStatus, setFilterStatus] = useState<string | null>(null)

  useEffect(() => {
    fetchData()
  }, [filterStatus])

  const fetchData = async () => {
    const statusParam = filterStatus ? `?status=${filterStatus}` : ''
    const [ticketRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/tickets/' + statusParam),
      fetch(API_BASE + '/tickets/statistics')
    ])
    if (ticketRes.ok) setTickets((await ticketRes.json()).tickets || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const selectTicket = async (ticket: Ticket) => {
    setSelectedTicket(ticket)
    const res = await fetch(API_BASE + '/tickets/' + ticket.id + '/comments')
    if (res.ok) setComments((await res.json()).comments || [])
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open': return 'bg-blue-100 text-blue-800'
      case 'in_progress': return 'bg-yellow-100 text-yellow-800'
      case 'closed': return 'bg-green-100 text-green-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800'
      case 'medium': return 'bg-yellow-100 text-yellow-800'
      case 'low': return 'bg-green-100 text-green-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">Support Tickets</h1>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">+ New Ticket</button>
        </div>

        {statistics && (
          <div className="grid grid-cols-6 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Tickets</div>
              <div className="text-2xl font-bold">{statistics.total_tickets}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Open</div>
              <div className="text-2xl font-bold text-blue-600">{statistics.open_tickets}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">In Progress</div>
              <div className="text-2xl font-bold text-yellow-600">{statistics.in_progress}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Closed Today</div>
              <div className="text-2xl font-bold text-green-600">{statistics.closed_today}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Avg Resolution</div>
              <div className="text-2xl font-bold">{statistics.avg_resolution_hours}h</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Satisfaction</div>
              <div className="text-2xl font-bold text-green-600">{statistics.satisfaction_rate}%</div>
            </div>
          </div>
        )}

        <div className="flex gap-4 mb-6">
          <button onClick={() => setFilterStatus(null)} className={`px-4 py-2 rounded-lg ${!filterStatus ? 'bg-blue-600 text-white' : 'bg-white border'}`}>All</button>
          <button onClick={() => setFilterStatus('open')} className={`px-4 py-2 rounded-lg ${filterStatus === 'open' ? 'bg-blue-600 text-white' : 'bg-white border'}`}>Open</button>
          <button onClick={() => setFilterStatus('in_progress')} className={`px-4 py-2 rounded-lg ${filterStatus === 'in_progress' ? 'bg-blue-600 text-white' : 'bg-white border'}`}>In Progress</button>
          <button onClick={() => setFilterStatus('closed')} className={`px-4 py-2 rounded-lg ${filterStatus === 'closed' ? 'bg-blue-600 text-white' : 'bg-white border'}`}>Closed</button>
        </div>

        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Tickets</h2></div>
            <div className="divide-y">
              {tickets.map(ticket => (
                <div key={ticket.id} className="p-4 hover:bg-gray-50 cursor-pointer" onClick={() => selectTicket(ticket)}>
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <span className="font-mono text-sm text-gray-500">{ticket.id}</span>
                      <div className="font-semibold">{ticket.subject}</div>
                    </div>
                    <div className="flex gap-2">
                      <span className={`px-2 py-1 rounded-full text-xs ${getPriorityColor(ticket.priority)}`}>{ticket.priority}</span>
                      <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(ticket.status)}`}>{ticket.status}</span>
                    </div>
                  </div>
                  <div className="text-sm text-gray-600 line-clamp-1">{ticket.description}</div>
                  <div className="flex gap-4 mt-2 text-xs text-gray-400">
                    <span>{ticket.customer_email}</span>
                    <span className="px-2 py-0.5 bg-gray-100 rounded capitalize">{ticket.category}</span>
                    <span>{new Date(ticket.created_at).toLocaleString()}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            {selectedTicket ? (
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex justify-between items-start mb-4">
                  <span className="font-mono text-sm text-gray-500">{selectedTicket.id}</span>
                  <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(selectedTicket.status)}`}>{selectedTicket.status}</span>
                </div>
                <h3 className="text-lg font-semibold mb-2">{selectedTicket.subject}</h3>
                <p className="text-sm text-gray-600 mb-4">{selectedTicket.description}</p>
                <div className="space-y-2 text-sm border-t pt-4">
                  <div className="flex justify-between"><span className="text-gray-500">Customer</span><span>{selectedTicket.customer_email}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Category</span><span className="capitalize">{selectedTicket.category}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Priority</span><span className={`px-2 py-1 rounded text-xs ${getPriorityColor(selectedTicket.priority)}`}>{selectedTicket.priority}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Assigned</span><span>{selectedTicket.assigned_to || 'Unassigned'}</span></div>
                </div>

                <div className="border-t mt-4 pt-4">
                  <h4 className="font-semibold mb-3">Comments</h4>
                  <div className="space-y-3 max-h-48 overflow-y-auto">
                    {comments.map(comment => (
                      <div key={comment.id} className={`p-3 rounded ${comment.is_internal ? 'bg-yellow-50' : 'bg-gray-50'}`}>
                        <div className="flex justify-between text-xs text-gray-500 mb-1">
                          <span>{comment.author}</span>
                          <span>{new Date(comment.created_at).toLocaleString()}</span>
                        </div>
                        <div className="text-sm">{comment.content}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4">Ticket Details</h3>
                <p className="text-gray-500 text-sm">Select a ticket to view details</p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  )
}
